import { Card, Select, Skeleton } from "antd";
import React, { useContext, useEffect, useState } from "react";
import Chart from "react-google-charts";
import { useQuery } from "react-query";
import { columnChartConfigurationOptions } from "../../config";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import CustomHeader from "../common/Header";

const supportProjects = [
    { id: 10067, name: "Aavgo Support And Operations" },
    { id: 10087, name: "Security Support" },
];
const { Option } = Select;

function SupportAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const [formData, setFormData] = useState({
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
    });
    const [selectedProject, setSelectedProject] = useState(
        supportProjects[0].id,
    );
    const {
        status: escalatedSupportStatus,
        data: escalatedSupportRes,
        refetch: getEscalatedUSSupportData,
    } = useQuery(
        "escalatedSupportData",
        async () =>
            await HTTP("/analytics/getEscalatedUSSupportData", {
                ...formData,
            }),
        { enabled: false },
    );
    const {
        status: escalatedTechSupportStatus,
        data: escalatedTechSupportRes,
        refetch: getEscalatedTechSupportData,
    } = useQuery(
        "escalatedTechSupportData",
        async () =>
            await HTTP("/analytics/getEscalatedTechSupportData", {
                ...formData,
            }),
        { enabled: false },
    );
    const {
        status: issueTypeSupportStatus,
        data: issueTypeSupportRes,
        refetch: getIssueTypeSupportData,
    } = useQuery(
        "issueTypeSupportData",
        async () =>
            await HTTP("/analytics/getIssueTypeSupportData", {
                ...formData,
            }),
        { enabled: false },
    );

    const {
        status: breachedTimeToResolutionSupportStatus,
        data: breachedTimeToResolutionSupportRes,
        refetch: getBreachedTimeToResolutionSupportData,
    } = useQuery(
        "breachedTimeToResolutionSupportData",
        async () =>
            await HTTP("/analytics/getBreachedTimeToResolutionSupportData", {
                ...formData,
            }),
        { enabled: false },
    );
    const {
        status: breachedTimeToReponseSupportStatus,
        data: breachedTimeToReponseSupportRes,
        refetch: getBreachedTimeToReponseSupportData,
    } = useQuery(
        "breachedTimeToReponseSupportData",
        async () =>
            await HTTP("/analytics/getBreachedTimeToReponseSupportData", {
                ...formData,
            }),
        { enabled: false },
    );
    const {
        status: supportProjectAssigneeTicketStatus,
        data: supportProjectAssigneeTicketsRes,
        refetch: getSupportProjectAssigneeTickets,
    } = useQuery(
        ["support-assignee-tickets", selectedProject],
        async () =>
            await HTTP("/analytics/getSupportProjectAssigneeTickets", {
                ...formData,
                project_id: selectedProject,
            }),
        { enabled: false },
    );

    const {
        status: shiftWiseTicketsStatus,
        data: shiftWiseTicketsRes,
        refetch: getShiftWiseTicketsData,
    } = useQuery(
        "shiftWiseTicket",
        async () =>
            await HTTP("/analytics/getShiftWiseTicketsData", {
                ...formData,
            }),
        { enabled: false },
    );

    useEffect(() => {
        const [start_date, end_date] = selectedDates;
        setFormData({
            ...formData,
            start_date: formatDate(start_date),
            end_date: formatDate(end_date),
        });
    }, [selectedDates]);

    useEffect(() => {
        getSupportProjectAssigneeTickets();
    }, [selectedProject]);

    useEffect(() => {
        getEscalatedTechSupportData();
        getEscalatedUSSupportData();
        getIssueTypeSupportData();
        getBreachedTimeToResolutionSupportData();
        getBreachedTimeToReponseSupportData();
        getSupportProjectAssigneeTickets();
        getShiftWiseTicketsData();
    }, []);

    useEffect(() => {
        getEscalatedTechSupportData();
        getEscalatedUSSupportData();
        getIssueTypeSupportData();
        getBreachedTimeToResolutionSupportData();
        getBreachedTimeToReponseSupportData();
        getSupportProjectAssigneeTickets();
        getShiftWiseTicketsData();
    }, [formData]);

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            <Card className="box-shadow margin-10">
                <div className="margin-10">
                    {escalatedSupportStatus !== "loading" &&
                    escalatedSupportStatus !== "idle" ? (
                        <Chart
                            chartType="ColumnChart"
                            data={escalatedSupportRes.data}
                            options={{
                                ...columnChartConfigurationOptions,
                                title: "Escalated Support",
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>
                <div className="margin-10">
                    {escalatedTechSupportStatus !== "loading" &&
                    escalatedTechSupportStatus !== "idle" ? (
                        <Chart
                            chartType="ColumnChart"
                            data={escalatedTechSupportRes.data}
                            options={{
                                ...columnChartConfigurationOptions,
                                title: "Escalated Tech Support",
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>
                <div className="margin-10">
                    {issueTypeSupportStatus !== "loading" &&
                    issueTypeSupportStatus !== "idle" ? (
                        <Chart
                            chartType="ColumnChart"
                            data={issueTypeSupportRes.data}
                            options={{
                                ...columnChartConfigurationOptions,
                                title: "Issue Type",
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>
                <div className="margin-10">
                    {breachedTimeToReponseSupportStatus !== "loading" &&
                    breachedTimeToReponseSupportStatus !== "idle" ? (
                        <Chart
                            chartType="LineChart"
                            data={breachedTimeToReponseSupportRes.data}
                            options={{
                                ...columnChartConfigurationOptions,
                                legend: { position: "bottom" },
                                title: "Breached Response Time",
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>
                <div className="margin-10">
                    {breachedTimeToResolutionSupportStatus !== "loading" &&
                    breachedTimeToResolutionSupportStatus !== "idle" ? (
                        <Chart
                            chartType="LineChart"
                            data={breachedTimeToResolutionSupportRes.data}
                            options={{
                                ...columnChartConfigurationOptions,
                                legend: { position: "bottom" },
                                title: "Breached Resolution Time",
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>

                <div className="margin-10">
                    <Select
                        value={selectedProject}
                        onChange={(id) => setSelectedProject(id)}
                    >
                        {supportProjects.map(({ id, name }) => (
                            <Option key={id} value={id}>
                                {name}
                            </Option>
                        ))}
                    </Select>
                    {supportProjectAssigneeTicketStatus !== "loading" &&
                    supportProjectAssigneeTicketStatus !== "idle" ? (
                        <Chart
                            chartType="BarChart"
                            data={supportProjectAssigneeTicketsRes.data}
                            height={"50vh"}
                            options={{
                                ...columnChartConfigurationOptions,
                                title: "Support Project Ticket",
                                chartArea: {
                                    width: "65%",
                                    ...columnChartConfigurationOptions.chartArea,
                                    isStacked: true,
                                },
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>
                <div className="margin-10">
                    {shiftWiseTicketsStatus !== "loading" &&
                    shiftWiseTicketsStatus !== "idle" ? (
                        <Chart
                            chartType="BarChart"
                            data={shiftWiseTicketsRes.data}
                            options={{
                                ...columnChartConfigurationOptions,
                                title: "Shift Wise Ticket",
                            }}
                        />
                    ) : (
                        <Skeleton title={false} paragraph={{ rows: 10 }} />
                    )}
                </div>
            </Card>
        </Card>
    );
}

export default SupportAnalytics;
